import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SERVICE_TYPES = ["Web Sitesi", "Mobil Uygulama", "Web Sitesi + Mobil Uygulama", "UI/UX Tasarım", "Diğer"];
const BUDGETS = ["₺10.000 – ₺25.000", "₺25.000 – ₺50.000", "₺50.000 – ₺100.000", "₺100.000+"];
const URGENCY = ["Esnek", "Normal (2-4 hafta)", "Acil (1 hafta içinde)"];

const initialForm = {
  full_name: "", email: "", phone: "", business_name: "",
  service_type: "", budget_range: "", urgency: "", project_description: "",
};

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-sm text-[#9CA3AF]">{label}</Label>
    {children}
  </div>
);

const inputCls = "bg-[#0F0E17] border-white/10 focus:border-[#10B981]/50 text-white placeholder:text-[#4B5563] h-12 rounded-xl";

const ApplicationForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setValue = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", form);
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="basvuru" className="px-6 lg:px-16 py-28 relative overflow-hidden">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#10B981]/[0.05] blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <span className="font-code text-xs text-[#10B981] tracking-[0.3em]">BAŞVURU</span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl mt-4 tracking-tight leading-[1.1]">
            Projeni anlat<span className="text-[#10B981]">,</span><br />
            başlayalım<span className="text-[#10B981]">.</span>
          </h2>
          <p className="text-[#9CA3AF] leading-relaxed mt-6 max-w-md">
            İşletmen için nasıl bir şey istediğini kendi cümlelerinle yaz.
            Formu gönderdikten sonra en geç 24 saat içinde telefon ya da
            e-posta ile sana ulaşırım.
          </p>

          <div className="mt-10 space-y-4">
            {[
              ["01", "Formu doldur — 2 dakika sürer"],
              ["02", "24 saat içinde seninle iletişime geçerim"],
              ["03", "İhtiyacına özel teklif ve yol haritası"],
            ].map(([n, t]) => (
              <div key={n} className="flex items-center gap-4">
                <span className="font-code text-sm text-[#F59E0B]">{n}</span>
                <span className="text-sm text-[#D1D5DB]">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                data-testid="submission-success-modal"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-[#151421] border border-[#10B981]/30 rounded-2xl p-12 text-center h-full flex flex-col items-center justify-center min-h-[480px]"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                >
                  <CheckCircle2 size={64} className="text-[#10B981]" />
                </motion.div>
                <h3 className="font-display font-bold text-3xl mt-8">Başvurun alındı!</h3>
                <p className="text-[#9CA3AF] mt-4 max-w-sm leading-relaxed">
                  Projeni inceleyip en geç 24 saat içinde telefon ya da e-posta ile sana döneceğim.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="form-new-application-button"
                  className="mt-10 flex items-center gap-2 border border-white/15 hover:border-[#10B981]/50 text-white px-6 py-3 rounded-full transition-colors duration-300"
                >
                  <RotateCcw size={15} />
                  Yeni Başvuru Oluştur
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                data-testid="application-form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="bg-[#151421]/80 backdrop-blur-md border border-white/[0.08] rounded-2xl p-8 md:p-10 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Ad Soyad *" htmlFor="full_name">
                    <Input id="full_name" data-testid="input-full-name" required minLength={2}
                      placeholder="Ayşe Yılmaz" value={form.full_name} onChange={set("full_name")} className={inputCls} />
                  </Field>
                  <Field label="İşletme Adı *" htmlFor="business_name">
                    <Input id="business_name" data-testid="input-business-name" required minLength={2}
                      placeholder="Yılmaz Kafe" value={form.business_name} onChange={set("business_name")} className={inputCls} />
                  </Field>
                  <Field label="E-posta *" htmlFor="email">
                    <Input id="email" type="email" data-testid="input-email" required
                      placeholder="ornek@isletme.com" value={form.email} onChange={set("email")} className={inputCls} />
                  </Field>
                  <Field label="Telefon *" htmlFor="phone">
                    <Input id="phone" type="tel" data-testid="input-phone" required minLength={7}
                      placeholder="05xx xxx xx xx" value={form.phone} onChange={set("phone")} className={inputCls} />
                  </Field>
                  <Field label="Hizmet Türü *" htmlFor="service_type">
                    <Select required value={form.service_type} onValueChange={setValue("service_type")}>
                      <SelectTrigger id="service_type" data-testid="select-service-type" className={inputCls}>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151421] border-white/10 text-white">
                        {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Bütçe Aralığı *" htmlFor="budget_range">
                    <Select required value={form.budget_range} onValueChange={setValue("budget_range")}>
                      <SelectTrigger id="budget_range" data-testid="select-budget-range" className={inputCls}>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151421] border-white/10 text-white">
                        {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Aciliyet *" htmlFor="urgency">
                    <Select required value={form.urgency} onValueChange={setValue("urgency")}>
                      <SelectTrigger id="urgency" data-testid="select-urgency" className={inputCls}>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151421] border-white/10 text-white">
                        {URGENCY.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Projeni Anlat * — İşletmen için nasıl bir şey istiyorsun?" htmlFor="project_description">
                  <Textarea
                    id="project_description"
                    data-testid="input-project-description"
                    required
                    minLength={10}
                    rows={6}
                    placeholder="Örn: Restoranım için online sipariş alabileceğim, menümü sergileyen bir web sitesi istiyorum. Ayrıca müşterilerim telefondan da kolayca sipariş verebilmeli..."
                    value={form.project_description}
                    onChange={set("project_description")}
                    className="bg-[#0F0E17] border-white/10 focus:border-[#10B981]/50 text-white placeholder:text-[#4B5563] rounded-xl resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="form-submit-button"
                  className="group w-full flex items-center justify-center gap-2.5 bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-60 text-[#08070D] font-semibold py-4 rounded-full transition-colors duration-300"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      Başvuruyu Gönder
                      <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default ApplicationForm;
